/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * NEW GOLD STANDARD — marketing-first, not technical-first.
 *
 * Rules for this page (and every feature page going forward):
 *   - Lead with the contractor's problem, not the product's features
 *   - Use contractor vocabulary: "bid," "scope," "markup," "the job"
 *   - Headers should match search queries contractors actually type
 *   - Outcomes before mechanisms: "bid in hours, not days" not "6-tab workspace"
 *   - Specific, credible, grounded: real trade examples, real scenarios
 *   - Subsystem docs inform WHAT — marketing copy explains WHY it matters
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  BarChart3,
  Calculator,
  FileText,
  GitBranch,
  Layers,
  Ruler,
  Send,
  Briefcase,
  DollarSign,
  Bot,
  Clock,
  ShieldCheck,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Construction Estimating Software",
  title: "Stop losing jobs to the guy",
  titleHighlight: "who prices faster than you.",
  description:
    "Your competitor isn't better at the work — they're faster at the bid. CC turns a 3-day estimating grind into a same-day turnaround: scope the work, measure the blueprints, price every line from your actual cost database, check your margins, and send a branded proposal the client can approve from their phone. One workspace. No spreadsheet. No re-entry between tools.",

  featureGrid: {
    title: "Why contractors switch from spreadsheets to CC",
    items: [
      {
        icon: Clock,
        title: "Bid the Same Day You Get the Plans",
        description:
          "Scope, takeoff, price, and send — in the same sitting. Stop losing jobs because your estimate took a week while your competitor's took an afternoon.",
      },
      {
        icon: Ruler,
        title: "Measure Directly Off the Blueprints",
        description:
          "Upload the PDF, set the scale, draw your areas and linear runs right on screen. No separate takeoff app, no exporting a CSV, no re-typing quantities.",
      },
      {
        icon: DollarSign,
        title: "Price From Your Real Costs, Not Guesses",
        description:
          "Every line item pulls from your cost database — your actual supplier prices, your labor rates, your equipment costs. Update a rate once, every future bid uses it.",
      },
      {
        icon: Layers,
        title: "Build Once, Reuse on Every Job",
        description:
          "Bundle materials, labor, and equipment into reusable work packages. A 'Patio Install' package. An 'RTU Replacement' package. Pull it into any estimate in one click.",
      },
      {
        icon: BarChart3,
        title: "Know Your Margin Before You Send",
        description:
          "See your effective markup, your break-even buffer, and what happens to your profit if material prices go up 10% — before the client ever sees a number.",
      },
      {
        icon: FileText,
        title: "Send a Proposal That Wins",
        description:
          "One click turns your estimate into a branded PDF your client can approve from their phone. Three templates. Your logo. Professional enough to beat the handwritten quote.",
      },
      {
        icon: ShieldCheck,
        title: "Numbers Lock When the Proposal Goes Out",
        description:
          "Once you send, the estimate locks. Nobody on your team can accidentally change a number after the client has seen it. When they say yes, that's the number.",
      },
      {
        icon: GitBranch,
        title: "Revise Without Losing the Original",
        description:
          "Client wants a change? Create a revision. The original stays intact. Every version is tracked. You always know what you sent and when.",
      },
      {
        icon: TrendingUp,
        title: "Every Job Makes the Next One Faster",
        description:
          "Actual costs from completed projects feed back into your cost database. Your first estimate is educated. Your hundredth is dialed in.",
      },
    ],
  },

  workflow: {
    title: "How to estimate a construction project in CC",
    description:
      "This is the workflow that turns a set of plans into a priced, branded proposal your client can sign. Five steps, one workspace, no switching between apps.",
    steps: [
      {
        label: "Scope",
        title: "Define what you're pricing",
        description:
          "Start by writing the scope of work — what's included, what's excluded, what assumptions you're making. If the client gave you an RFP or spec document, upload it and pull the scope directly instead of retyping. This is the step that saves you from the 'I thought that was included' conversation three weeks into the job.",
        details: [
          "Write the scope directly or import from an uploaded RFP / SOW",
          "Set up work areas for how the job is actually structured (front yard, Level 2, Phase 1)",
          "Attach documents so everything lives with the estimate",
          "This scope carries through to the proposal your client sees",
        ],
      },
      {
        label: "Takeoff",
        title: "Measure the plans without leaving the app",
        description:
          "Upload the blueprint PDF, set the scale, and draw measurements right on screen — areas, linear runs, counts, volumes. The quantities feed directly into your estimate. No separate takeoff app, no exporting numbers to a spreadsheet, no retyping. One set of measurements, one source of truth.",
        details: [
          "Upload PDF or image blueprints with multi-page plan set support",
          "Area, linear, count, and volume measurement types",
          "Measurements organize by work area so your estimate is structured from the start",
          "Hit 'Populate Builder' and every measurement becomes a priced line item",
        ],
      },
      {
        label: "Price",
        title: "Build the estimate from your actual costs",
        description:
          "This is where the money is. Every line item in your estimate pulls from your cost database — real supplier prices, real labor rates, real equipment costs. Organize by work area. Apply your overhead, margin, contingency, and tax settings. Browse your catalog inline and add items without leaving the page. If you've built a work package for a job type you do regularly, pull it in and the whole assembly expands into line items automatically.",
        details: [
          "Line items organized by work area with phases for complex jobs",
          "Pull from your cost database — materials, labor, equipment, work packages",
          "Overhead, margin, contingency, and tax configurable per cost type",
          "Per-estimate overrides when a job needs different numbers than your org default",
          "Import from Excel if you're migrating an existing estimate",
        ],
      },
      {
        label: "Analyze",
        title: "Check the numbers before the client sees them",
        description:
          "Before you send anything, CC runs a financial analysis on your estimate. What's your effective markup? What's your break-even buffer — how much can costs rise before you lose money? Which three line items are driving the most cost, and what happens to your profit if material prices go up 10%? This is the tab that catches the estimate you were about to send at 12% margin when your target is 20%.",
        details: [
          "Effective markup and margin calculated from your actual line items",
          "Break-even buffer — how much room you have before the job loses money",
          "Cost sensitivity scenarios: what if costs go up 5%, 10%, 15%, 25%?",
          "Top cost drivers ranked by impact so you know where to negotiate",
        ],
      },
      {
        label: "Send",
        title: "One click from estimate to branded proposal",
        description:
          "Pick a template (Standard, Cover Page, or Minimal), edit the scope language inline, configure which pricing details the client sees, and hit send. Your estimate becomes a branded PDF the client reviews in a portal on their phone. When they accept, the estimate moves to Won automatically. You never ask 'did they get it?' because you can see when they opened it and how long they reviewed it.",
        details: [
          "Three proposal templates with your branding and logo",
          "Inline editing — change the scope language without going back to the estimate",
          "Choose how much pricing detail to show the client (summary vs. line items)",
          "Client accepts or declines from a portal link — no login required",
          "Engagement tracking: you know when they opened it and how long they spent",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: DollarSign,
      title: "Your Costs, Not Generic Data",
      description:
        "The #1 reason contractors underbid is pricing from memory or industry averages instead of their actual costs. CC ships with a cost database pre-loaded for your trade, then gets sharper every time you complete a job — because actual costs from finished projects feed back into the database automatically. Your first estimate is educated. Your hundredth is dialed in. And when a supplier raises prices, you update the rate once and every future estimate reflects reality.",
      bullets: [
        "Pre-loaded cost database starter sets for common contractor trades",
        "Your supplier prices, your labor rates, your equipment costs — not generic industry averages",
        "Actual project costs feed back into the database so rates get more accurate over time",
        "Update a price once, every future estimate uses the new number",
      ],
    },
    {
      icon: BarChart3,
      title: "Know If You're Making Money Before You Commit",
      description:
        "Most contractors find out they underpriced a job after it's done. CC's analytics tab shows you the answer before you send anything: your effective markup, your break-even buffer (how much costs can rise before you lose money), which line items are driving the most risk, and what happens to your profit under different cost-increase scenarios. The estimate that 'looked fine' at a glance shows up at 11% margin when your target is 20% — and you catch it before the client sees a number.",
      bullets: [
        "Effective markup calculated from your actual line items, not a top-line assumption",
        "Break-even buffer shows how much room you have before the job loses money",
        "Sensitivity analysis: profit impact at 5%, 10%, 15%, and 25% cost increases",
        "Top cost drivers ranked so you know where to push back on suppliers or adjust scope",
      ],
    },
    {
      icon: Layers,
      title: "Stop Re-Pricing the Same Work on Every Bid",
      description:
        "If you install patios every week, you shouldn't be building a patio estimate from scratch every time. CC's work packages let you bundle materials, labor, and equipment into reusable packages — a 'Paver Patio' package, a 'French Drain' package, an 'RTU Replacement' package — with your production rates and waste factors already baked in. Pull the package into any estimate and it expands into detailed line items automatically. Build it once, reuse it forever, update it when prices change.",
      bullets: [
        "Bundle materials + labor + equipment into one reusable package",
        "Production rates and waste factors built in — not re-calculated every time",
        "Pull into any estimate in one click — the work package expands into line items",
        "Track package usage across estimates so high-value templates are easy to maintain",
      ],
    },
    {
      icon: FileText,
      title: "Proposals That Look Like You Meant It",
      description:
        "The contractor who sends a branded, professionally formatted proposal wins more work than the one who emails a spreadsheet — even when the spreadsheet contractor has a better price. CC turns your estimate into a client-facing proposal in one click: your logo, your branding, three template options, inline scope editing, and a client portal where the customer can accept or decline from their phone without creating an account. You know when they opened it and how long they spent reviewing it.",
      bullets: [
        "Three proposal templates: Standard, Cover Page, Minimal — all with your branding",
        "Edit scope language inline without going back to the estimate",
        "Client portal: accept or decline from a link, no login required",
        "Engagement tracking: view count and time spent per proposal",
      ],
    },
  ],

  connections: {
    title: "The estimate is the start, not the end",
    items: [
      {
        icon: Send,
        flow: "Estimate → Proposal → Won Job",
        description:
          "Your estimate becomes a branded proposal. Client accepts from their phone. The estimate moves to Won and you're ready to create the project — no data re-entry.",
      },
      {
        icon: Briefcase,
        flow: "Won Estimate → Project with Budget",
        description:
          "When the client says yes, your estimate becomes the project budget. Every line item becomes a cost tracking target. You know what you bid and you track what you spend.",
      },
      {
        icon: DollarSign,
        flow: "Finished Job → Smarter Cost Database",
        description:
          "Actual costs from completed projects feed back into your cost database. What you actually paid for concrete this year makes next year's concrete estimate more accurate.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Blueprint Takeoffs",
      href: "/features/takeoffs",
      icon: Ruler,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: DollarSign,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: Calculator,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: Bot,
    },
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Solutions by Trade",
      href: "/solutions",
      icon: Ruler,
    },
    {
      label: "AI for General Contractors",
      href: "/ai/for-general-contractors",
      icon: Sparkles,
    },
    {
      label: "Compare vs PlanSwift",
      href: "/compare/planswift",
      icon: BarChart3,
    },
  ],
};

export function EstimatesPageClient() {
  return <FeaturePageTemplate config={config} />;
}
